import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/components/CookieConsent";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, RefreshCw, AtSign } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const MOVIE_WORDS = [
  "Matrix", "Jedi", "Hobbit", "Groot", "Gandalf", "Neo", "Ripley", "Maverick",
  "Rocky", "Indy", "Marty", "Doc", "Yoda", "Spock", "Kirk", "Thor",
  "Loki", "Stark", "Fury", "Morpheus", "Trinity", "Joker", "Batman",
  "Pixel", "Reel", "Flick", "Scene", "Popcorn", "Cinema", "Montage",
  "Sequel", "Frame", "Script", "Dolby", "Retro", "Debut", "Epic",
  "Nova", "Cosmo", "Orbit", "Neon", "Prism", "Sonic", "Turbo",
];

const generateUsername = () => {
  const word = MOVIE_WORDS[Math.floor(Math.random() * MOVIE_WORDS.length)];
  const num = Math.floor(100 + Math.random() * 900);
  return `${word}${num}`;
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const suggestedUsername = (location.state as any)?.suggestedUsername;
  const forgotFromState = (location.state as any)?.forgotPassword;
  const [isLogin, setIsLogin] = useState(suggestedUsername ? false : true);
  const [isForgotPassword, setIsForgotPassword] = useState(!!forgotFromState);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState(() => suggestedUsername || generateUsername());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);

  const handleForgotPassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setMessage("");
    if (!email) { setError(t("resetPassword.enterEmail", "Please enter your email address.")); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setMessage(t("resetPassword.emailSent", "Check your email for a password reset link."));
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [email, t]);

  useEffect(() => {
    if (user) navigate("/home");
  }, [user, navigate]);

  if (user) return null;

  const validateUsername = (v: string) => {
    if (v.length < 3) return t("landing.usernameRequired");
    if (v.length > 24) return t("landing.usernameTooLong");
    if (!/^[a-zA-Z0-9_]+$/.test(v)) return t("landing.usernameInvalid");
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setMessage("");
    if (!isLogin) {
      const usernameErr = validateUsername(username.trim());
      if (usernameErr) { setError(usernameErr); return; }
      if (!agreedPrivacy || !agreedTerms) { setError(t("auth.mustAgree", "You must agree to both the Privacy Policy and Terms of Service.")); return; }
    }
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackEvent("login", { method: "email" });
        navigate("/home");
      } else {
        const { data: exists } = await supabase.rpc("check_username_exists", { _username: username.trim() });
        if (exists) { setError(t("landing.usernameTaken")); setLoading(false); return; }
        const currentLang = (window.localStorage.getItem('ayeris_language') || navigator.language || 'en').split('-')[0];
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: `${window.location.origin}/home`, data: { preferred_username: username.trim(), language: currentLang } },
        });
        if (signUpError) throw signUpError;
        if (signUpData.user) {
          await (supabase.from("profiles") as any).update({ username: username.trim() }).eq("id", signUpData.user.id);
        }
        trackEvent("sign_up", { method: "email" });
        setMessage(t("landing.checkEmail"));
      }
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Forgot Password View ───
  if (isForgotPassword) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-6">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-foreground">
              {t("resetPassword.title", "Reset Password")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("resetPassword.subtitle", "Enter your email and we'll send you a reset link.")}
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("auth.emailLabel")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="you@example.com" />
            </div>

            {error && <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}
            {message && <p className="text-xs text-primary bg-primary/10 rounded-lg px-3 py-2">{message}</p>}

            <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("resetPassword.sendLink", "Send Reset Link")}
            </button>
          </form>

          <div className="text-center">
            <button onClick={() => { setIsForgotPassword(false); setError(""); setMessage(""); }} className="text-sm text-primary font-medium">
              {t("resetPassword.backToSignIn", "Back to Sign In")}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-6">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-foreground">
            {isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isLogin ? t("auth.signInToAccount") : t("auth.joinToSave")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("auth.usernameLabel")}</label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={24} className="w-full pl-9 pr-12 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder={t("auth.usernamePlaceholder")} />
                <button type="button" onClick={() => setUsername(generateUsername())} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-muted transition-colors" title={t("landing.generateRandom")}>
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t("auth.usernameHint")}</p>
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("auth.emailLabel")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("auth.passwordLabel")}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="••••••••" />
          </div>

          {isLogin && (
            <div className="text-right -mt-2">
              <button type="button" onClick={() => { setIsForgotPassword(true); setError(""); setMessage(""); }} className="text-xs text-primary hover:underline">
                {t("auth.forgotPassword")}
              </button>
            </div>
          )}

          {!isLogin && (
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="agree-privacy"
                  checked={agreedPrivacy}
                  onCheckedChange={(v) => setAgreedPrivacy(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="agree-privacy" className="text-xs text-muted-foreground leading-snug">
                  {t("auth.iHaveRead", "I have read and agree to the")}{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {t("auth.privacyPolicyLink", "Privacy Policy")}
                  </a>
                </label>
              </div>
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="agree-terms"
                  checked={agreedTerms}
                  onCheckedChange={(v) => setAgreedTerms(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="agree-terms" className="text-xs text-muted-foreground leading-snug">
                  {t("auth.iHaveRead", "I have read and agree to the")}{" "}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {t("auth.termsOfServiceLink", "Terms of Service")}
                  </a>
                </label>
              </div>
            </div>
          )}

          {error && <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}
          {message && <p className="text-xs text-primary bg-primary/10 rounded-lg px-3 py-2">{message}</p>}

          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isLogin ? t("auth.signIn") : t("auth.signUp")}
          </button>
        </form>

        <div className="text-center">
          <button onClick={() => { setIsLogin(!isLogin); setError(""); setMessage(""); }} className="text-sm text-primary font-medium">
            {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}
          </button>
        </div>

        <button onClick={() => navigate("/home")} className="w-full text-center text-xs text-muted-foreground">
          {t("auth.continueWithout")}
        </button>
      </motion.div>
    </div>
  );
};

export default Auth;
